/* eslint-disable @typescript-eslint/no-explicit-any */
import { useLocation } from 'react-router-dom';
import { useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { ColumnDef } from '@tanstack/react-table';
import { Form } from 'react-bootstrap';
import FeatherIcon from 'feather-icons-react';

import PageBreadcrumb from 'components/common/PageBreadcrumb';
import { newApplicationsBreadcrumbItems } from 'data/newapplications';
import NewApplicationsTableSearch from './NewApplicationsTableSearch';
import orderItemService from 'service/orderItem';
import orderService from 'service/order';
import orderOutService from 'service/orderOut';
import { showAlert } from 'store/alert/alert.thunk';
import Button from 'components/base/Button';
import useFocusRefs from 'utils/useFocusRefs';
import legalEntitySettingsService from 'service/legalEntitySettings';

import './styles.scss';
import customerEntityService from 'service/charterer';
import { useAppContext } from 'providers/AppProvider';
import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import AdvanceTable from 'components/base/AdvanceTable';
import AdvanceTableFooter from 'components/base/AdvanceTableFooter';

export type NewApplicationsInfo = {
  product_name: string;
  barcode: string;
  unit: string;
  currency: string;
  current_balance: string;
  amount: string;
  full_cost: any;
  measure_unit_pid: string;
  package_quantity: any;
  measure_unit_kind_pid: number;
  gupid: number;
};
const NewApplicatons = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { pathname, state } = useLocation();
  const newOrderPid = Number(pathname.split('/').pop() || 0);
  const { sender_pid, currency_pid } = state;
  const dispatch: Dispatch<any> = useDispatch();
  const { setRef, focusRef } = useFocusRefs();
  const {
    config: { theme }
  } = useAppContext();

  const currencyId = useSelector(
    (state: any) => state?.auth?.legal_entity?.currency_id
  );

  const legalEntityId = useSelector(
    (state: any) => state?.auth?.legal_entity?.id
  );

  const [saveLoading, setSaveLoading] = useState(false);
  const [sendLoading, setSendLoading] = useState(false);
  const [orderItemData, setOrderItemData] = useState<any>([]);
  const [isAutoTab, setIsAutoTab] = useState(false);

  const { data } = useQuery(
    ['GET_ORDER_ITEMS', newOrderPid],
    () => {
      if (newOrderPid) {
        const res = orderItemService
          .getOrderItemOutById(newOrderPid, {
            offset: 0,
            limit: 10000
          })
          .then((res: any) => {
            return res?.order_items;
          });
        return res;
      }
    },
    { enabled: !!newOrderPid }
  );

  useMemo(() => {
    if (data) {
      const newData =
        data?.map((items: any) => {
          const packageQuantity = items?.package_quantity || 1;
          const quantity = items?.quantity_asked
            ? Number(items?.quantity_asked.toFixed(3))
            : 0;

          const fullPackages = Math.floor(quantity / packageQuantity);
          const remainingQuantity = Math.floor(quantity % packageQuantity);
          const quantityDisplay = `${fullPackages}/${remainingQuantity}`;

          return {
            product_name: items?.nomenclature?.name,
            barcode: items?.nomenclature?.barcode,
            unit: items?.nomenclature?.measure_unit?.name?.[i18n?.language],
            measure_unit_pid: items?.nomenclature?.measure_unit_pid || 0,
            amount:
              items?.nomenclature?.measure_unit_kind_pid === 1
                ? quantityDisplay
                : items?.quantity_asked || 0,
            full_cost: items?.price_wholesale ?? 0,
            nomenclature_pid: items?.nomenclature_pid || 0,
            package_quantity:
              items?.package_quantity !== undefined
                ? items.package_quantity
                : 1,
            product_pid: items?.product_pid || 0,
            product_parent_pid: items?.product_pid || 0,
            price: items?.price ?? 0,
            price_in: items?.price_in ?? 0,
            price_whosale: items?.price_whosale ?? 0,
            is_vat: items?.is_vat ? items?.is_vat : false,
            currency: items?.currency?.name?.[i18n?.language],
            currency_pid: items?.currency_pid,
            measure_unit_kind_pid:
              items?.nomenclature?.measure_unit_kind_pid || 0,
            gupid: items?.pid || 0
          };
        }) ?? [];

      setOrderItemData([...orderItemData, ...newData]);

      return newData;
    }
  }, [data, t, i18n?.language]);

  const { data: chartererData } = useQuery(
    [
      'GET_CHARTERER_CHECK_LIMIT',
      currencyId,
      legalEntityId,
      sender_pid,
      currency_pid
    ],
    () => {
      if (legalEntityId !== '' && sender_pid !== 0 && currency_pid !== 0) {
        const resData = customerEntityService
          .getByIdCheckChartererLimit(sender_pid, legalEntityId, {
            limit_type: 'AMOUNT',
            currency_pid: currency_pid
          })
          .then((res: any) => {
            return res;
          });
        return resData;
      }
    },
    {
      enabled: !!legalEntityId && !!sender_pid && !!currency_pid
    }
  );

  useQuery(['GET_SETTINGS'], async () => {
    await legalEntitySettingsService
      .getList({
        offset: 0,
        limit: 20
      })
      .then((res: any) => {
        setIsAutoTab(res?.legal_entity_settings[0].is_auto_tab ?? 0);
      });
  });

  const handleRemoveProductItem = (pid: number) => {
    if (!pid) return;

    orderItemService.deleteOrderItemOut(pid)?.then((res: any) => {
      if (res) {
        // Remove item locally
        setOrderItemData((prev: any) =>
          prev.filter((item: any) => item.gupid !== pid)
        );

        dispatch(
          showAlert({
            title: t('product_removed_successfully'),
            type: 'success'
          })
        );
      }
    });
  };

  const newApplicationsDataTableColumns: ColumnDef<NewApplicationsInfo>[] = [
    {
      id: 'action',
      cell: rowData => (
        <Button
          variant="hover"
          onClick={() => handleRemoveProductItem(rowData.row.original.gupid)}
        >
          <FeatherIcon
            icon="trash-2"
            className="cursor-pointer text-danger"
            size={20}
          />
        </Button>
      )
    },
    {
      accessorKey: 'order_number',
      header: '№',
      cell: rowData => {
        const orderNumber = rowData.row.index + 1;
        return <div>{orderNumber}</div>;
      },
      meta: {
        headerProps: { style: { width: '2%' } },
        cellProps: { className: 'text-900 text-center' }
      }
    },
    {
      accessorKey: 'product_name',
      header: t('product_names'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'barcode',
      header: t('barcode'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'unit',
      header: t('measure_unit'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'currency',
      header: t('currency'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'amount',
      header: t('quantity'),
      cell: rowData => {
        const amount: any = rowData.getValue() ?? '0';
        const measure_unit_kind_pid: any =
          rowData.row.original.measure_unit_kind_pid;

        const packageQuantity = rowData?.row?.original?.package_quantity || 1;
        /* prettier-ignore */
        const formatWithSpaces = (val: any) => {
          // Convert to string and provide fallback
          const cleanValue = String(val || '').replace(/[^0-9,./]/g, ''); // Remove non-numeric chars except ,./
          if (!cleanValue) return '';
          // Split on decimal or fraction for separate formatting
          const [integerPart, decimalPart] = cleanValue.includes('.')
            ? cleanValue.split('.')
            : cleanValue.includes('/')
              ? cleanValue.split('/')
              : [cleanValue, ''];
          const formattedInteger = integerPart.replace(
            /\B(?=(\d{3})+(?!\d))/g,
            ' '
          );
          return decimalPart
            ? `${formattedInteger}${cleanValue.includes('.') ? '.' : '/'}${decimalPart}`
            : formattedInteger;
        };

        // Function to remove spaces for state storage
        const removeSpaces = (val: string) => {
          return val.replace(/\s/g, '');
        };

        const [value, setValue] = useState(
          measure_unit_kind_pid === 1
            ? String(amount)
            : formatWithSpaces(String(amount))
        );

        const calculateAdjustedQuantity = (quantity: string) => {
          let integerPart = 0;
          let numerator = 0;
          let denominator = 0;

          const quantityStr = removeSpaces(String(quantity));

          if (quantityStr?.includes('/')) {
            [numerator, denominator] = quantityStr.split('/').map(Number);
            integerPart = numerator;
          } else {
            integerPart = +quantityStr;
          }

          return measure_unit_kind_pid === 1
            ? integerPart * packageQuantity + denominator
            : integerPart;
        };

        const onBlur = (e: React.ChangeEvent<HTMLInputElement>, index: any) => {
          const { name, value } = e.target;
          const cleanValue = removeSpaces(value); // Remove spaces for state
          const formattedValue = cleanValue.replace(/[^0-9,./]/g, '');

          const adjustedQuantity = calculateAdjustedQuantity(formattedValue);

          const updatedItems = orderItemData.map((item: any, i: any) => {
            if (i === +index) {
              return { ...item, [name]: formattedValue, adjustedQuantity };
            }
            return item;
          });

          setOrderItemData(updatedItems);
          setValue(formattedValue);
        };

        const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
          const newValue = e.target.value;
          const cleanValue = removeSpaces(newValue);

          // const numberPattern = /^-?\d*\.?\d*$/;
          // const remainderPattern = /^-?\d*\/?\d*$/;

          const integerPattern = /^-?\d*$/;
          const fractionPattern = /^-?\d*\/?\d*$/;
          const decimalPattern = /^-?\d*[.,]?\d*$/;

          // if (measure_unit_pid === 1) {
          //   if (remainderPattern.test(newValue)) {
          //     setValue(newValue); // Set value if fraction is valid
          //   }
          // } else {
          //   // Handle normal numbers
          //   if (numberPattern.test(newValue)) {
          //     setValue(newValue);
          //   }
          // }

          const packageQuantityNumber = Number(packageQuantity);

          if (measure_unit_kind_pid === 2) {
            if (
              integerPattern.test(cleanValue) &&
              !cleanValue.includes('.') &&
              !cleanValue.includes(',') &&
              !cleanValue.includes('/')
            ) {
              setValue(formatWithSpaces(cleanValue));
            }
          } else if (measure_unit_kind_pid === 1) {
            if (
              fractionPattern.test(cleanValue) &&
              !cleanValue.includes('.') &&
              !cleanValue.includes(',')
            ) {
              const [, denominator] = newValue.split('/').map(Number);
              if (
                denominator > packageQuantityNumber ||
                denominator === packageQuantityNumber
              ) {
                return; // Do nothing if the denominator is invalid
              }
              setValue(cleanValue);
            }
          } else {
            if (decimalPattern.test(newValue)) {
              setValue(cleanValue);
            }
          }
        };

        const updateProductInvoiceData = (
          name: string,
          value: string,
          index: any
        ) => {
          const cleanValue = removeSpaces(value);
          const updatedItems = orderItemData.map((item: any, i: any) => {
            if (i === +index) {
              return { ...item, [name]: cleanValue };
            }
            return item;
          });
          setOrderItemData(updatedItems);
        };

        const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
          if (e.key === 'Tab') {
            e.preventDefault();
            const { name, value } = e.target as HTMLInputElement;
            try {
              updateProductInvoiceData(name, value, rowData.row.index);
            } catch (error) {
              console.log('error===>', error);
            }
            const currentRowIndex = rowData.row.index;
            const nextRowIndex = currentRowIndex + 1;

            if (nextRowIndex < orderItemData?.length) {
              setTimeout(() => {
                focusRef(`amount_${nextRowIndex}`);
              }, 500);
            } else {
              setTimeout(() => {
                focusRef(`amount_0`);
              }, 500);
            }
          }
        };

        return (
          <div>
            <Form.Group>
              <Form.Control
                type="text"
                name="amount"
                value={value}
                onChange={handleChange}
                onBlur={(e: any) => onBlur(e, rowData.row.index)}
                onFocus={e => e.target.select()}
                onKeyDown={handleKeyDown}
                ref={setRef(`amount_${rowData.row.index}`)}
              />
            </Form.Group>
          </div>
        );
      },
      meta: {
        headerProps: { style: { width: '10%' } },
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'full_cost',
      header: t('price_admission'),
      cell: rowData => {
        const { full_cost } = rowData.row.original;

        return (
          <>
            {full_cost
              ? new Intl.NumberFormat('en-US', {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 4,
                  useGrouping: true
                })
                  .format(full_cost)
                  .replace(/,/g, ' ')
              : 0}
          </>
        );
      },
      meta: {
        cellProps: { className: 'text-900' }
      }
    }
  ];

  const table = useAdvanceTable({
    data: orderItemData || [],
    columns: newApplicationsDataTableColumns,
    pageSize: 10,
    pagination: true,
    sortable: true,
    selection: false
  });

  const sumOfPriceWholsale = orderItemData?.reduce((acc: any, item: any) => {
    const quantity = String(item?.amount) || '0';

    let integerPart = 0;
    let numerator = 0;
    let denominator = 0; // Default denominator to 1 if no fraction

    // Check if quantity contains '/'
    // eslint-disable-next-line
    if (quantity?.includes('/')) {
      // eslint-disable-next-line
      [numerator, denominator] = quantity?.split('/').map(Number);
      integerPart = numerator; // Whole number part
      // eslint-disable-next-line
      denominator = denominator; // Remainder of the division
    } else {
      integerPart = +quantity;
    }

    // Package quantity
    const packageQuantity = +item?.package_quantity || 1;
    // Calculate the fractional proportion

    const adjustedQuantity =
      item?.measure_unit_kind_pid === 1
        ? integerPart * packageQuantity + denominator
        : integerPart;

    const packageTotalCount =
      item?.measure_unit_kind_pid === 1
        ? (+item.full_cost / packageQuantity) * +adjustedQuantity
        : +item.full_cost * +adjustedQuantity;

    const price = item?.full_cost === undefined ? 0 : packageTotalCount;
    return acc + price;
  }, 0);

  const handleSave = () => {
    const createData = orderItemData?.map((el: any) => {
      const quantity = el?.amount || '0';

      let integerPart = 0;
      let numerator = 0;
      let denominator = 0;

      const quantityStr = String(quantity);

      if (quantityStr?.includes('/')) {
        [numerator, denominator] = quantityStr.split('/').map(Number);

        integerPart = numerator; // Whole number part
        // eslint-disable-next-line
        denominator = denominator; // Remainder of the division
      } else {
        integerPart = +quantityStr;
      }

      const packageQuantity = +el?.package_quantity || 1;

      const adjustedQuantity =
        el?.measure_unit_kind_pid === 1
          ? integerPart * packageQuantity + denominator
          : integerPart;

      return {
        barcode: el?.barcode,
        name: el?.product_name,
        price_wholesale: el?.full_cost,
        quantity_asked:
          el?.measure_unit_kind_pid === 1 ? +adjustedQuantity : +quantity,
        currency_pid: el?.currency_pid || 0,
        measure_unit_pid: el?.measure_unit_pid || 0,
        nomenclature_pid: el?.nomenclature_pid || 0,
        product_pid: el?.product_pid || 0,
        package_quantity: el?.package_quantity,
        product_parent_pid: el?.product_parent_pid || 0,
        price: el?.price,
        price_in: el?.price_in,
        price_whosale: el?.price_whosale,
        state: 1
      };
    });

    const updateData = {
      order_pid: newOrderPid || 0,
      order_items: createData
    };

    const { amount_limit } = chartererData || {};

    if (amount_limit < sumOfPriceWholsale) {
      dispatch(
        showAlert({
          title: t('you_have_amount_limit')
        })
      );
      setSaveLoading(false);
      return;
    }

    if (
      newOrderPid !== 0 &&
      (amount_limit === undefined ||
        amount_limit === 0 ||
        amount_limit >= sumOfPriceWholsale)
    ) {
      setSaveLoading(true);
      orderService.getOrderById(newOrderPid).then((res: any) => {
        if (res) {
          orderOutService
            .updateOrderOut(newOrderPid, {
              ...res,
              state: 1
            })
            .then((outresponse: any) => {
              if (outresponse) {
                orderItemService
                  .createOrderItem(updateData)
                  .then((createres: any) => {
                    if (createres) {
                      dispatch(
                        showAlert({
                          title: t('new_application_saved'),
                          type: 'success'
                        })
                      );
                      setSaveLoading(false);
                    }
                  });
              }
            });
        }
      });
    }
  };

  const handleSend = () => {
    const createData = orderItemData?.map((el: any) => {
      const quantity = el?.amount || '0';

      let integerPart = 0;
      let numerator = 0;
      let denominator = 0;

      const quantityStr = String(quantity);

      if (quantityStr?.includes('/')) {
        [numerator, denominator] = quantityStr.split('/').map(Number);

        integerPart = numerator; // Whole number part
        // eslint-disable-next-line
        denominator = denominator; // Remainder of the division
      } else {
        integerPart = +quantityStr;
      }

      const packageQuantity = +el?.package_quantity || 1;
      // Calculate the fractional proportion

      // Calculate adjusted quantity
      const adjustedQuantity =
        el?.measure_unit_kind_pid === 1
          ? integerPart * packageQuantity + denominator
          : integerPart;

      return {
        barcode: el?.barcode,
        name: el?.product_name,
        price_wholesale: el?.full_cost,
        quantity_asked:
          el?.measure_unit_kind_pid === 1 ? +adjustedQuantity : +quantity,
        currency_pid: el?.currency_pid || 0,
        measure_unit_pid: el?.measure_unit_pid || 0,
        state: 2,
        nomenclature_pid: el?.nomenclature_pid || 0,
        product_pid: el?.product_pid || 0,
        package_quantity: el?.package_quantity,
        product_parent_pid: el?.product_parent_pid || 0,
        price: el?.price,
        price_in: el?.price_in,
        price_whosale: el?.price_whosale
      };
    });

    // Check for empty name
    const invalidNameItems = createData
      .map((item: any, index: number) => ({
        ...item,
        orderNumber: index + 1
      }))
      .filter((item: any) => item?.quantity_asked === 0);

    if (invalidNameItems?.length > 0) {
      const invalidOrderNumbers = invalidNameItems
        .map((item: any) => item.orderNumber)
        .join(', ');
      /* prettier-ignore */
      dispatch(
        showAlert({
          title: `${t('you_have_no_quantity_in_this_item')}: ${invalidOrderNumbers}`,
          type: 'error'
        })
      );
      return;
    }

    const updateData = {
      order_pid: newOrderPid || 0,
      order_items: createData
    };

    const { amount_limit } = chartererData || {};

    if (amount_limit < sumOfPriceWholsale) {
      dispatch(
        showAlert({
          title: t('you_have_amount_limit')
        })
      );
      setSendLoading(false);
      return;
    }

    if (
      newOrderPid !== 0 &&
      createData?.length > 0 &&
      (amount_limit >= sumOfPriceWholsale ||
        amount_limit === 0 ||
        amount_limit === undefined)
    ) {
      setSendLoading(true);
      orderService.getOrderById(newOrderPid).then((res: any) => {
        if (res) {
          orderOutService
            .updateOrderOut(newOrderPid, {
              ...res,
              state: 2
            })
            .then((outresponse: any) => {
              if (outresponse) {
                orderItemService
                  .createOrderItem(updateData)
                  .then((createres: any) => {
                    if (createres) {
                      setSendLoading(false);
                      dispatch(
                        showAlert({
                          title: t('new_application_sent'),
                          type: 'success'
                        })
                      );
                      navigate('/applications/outgoing');
                    }
                  });
              }
            });
        }
      });
    } else {
      dispatch(
        showAlert({
          title: t('Unable_submit_request')
        })
      );
    }
  };

  return (
    <div>
      <PageBreadcrumb items={newApplicationsBreadcrumbItems} />
      <div>
        <h2 className="mb-5">{t('new_application')}</h2>

        <NewApplicationsTableSearch
          senderPid={sender_pid}
          orderItemData={orderItemData}
          setOrderItemData={setOrderItemData}
          focusRef={focusRef}
          isAutoTab={isAutoTab}
          chartererData={chartererData}
        />

        <AdvanceTableProvider {...table}>
          <div className="mt-4 mx-n4 px-4 mx-lg-n6 px-lg-6 bg-white border-top border-bottom border-300">
            <AdvanceTable tableProps={{ className: 'phoenix-table fs-9' }} />
            <AdvanceTableFooter pagination />
          </div>
        </AdvanceTableProvider>

        <div
          className={
            theme === 'dark' ? 'dark-footer-app-info' : 'footer-app-info'
          }
        >
          <div className="box">
            <p className="mb-0 text-bold">{t('amount_limit')}:</p>
            <span>
              {chartererData?.amount_limit
                ? new Intl.NumberFormat('en-US', {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 4,
                    useGrouping: true
                  })
                    .format(chartererData?.amount_limit)
                    .replace(/,/g, ' ')
                : 0}
            </span>
          </div>

          <div className="box">
            <p className="mb-0 text-bold">{t('total_price_admission')}:</p>
            <span>
              {sumOfPriceWholsale
                ? new Intl.NumberFormat('en-US', {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 4,
                    useGrouping: true
                  })
                    .format(sumOfPriceWholsale)
                    .replace(/,/g, ' ')
                : 0}
            </span>
          </div>

          <div className="main-btn-group">
            <Button
              type="button"
              variant="outline-primary"
              onClick={() => handleSave()}
              loading={saveLoading}
            >
              {t('save')}
            </Button>
            <Button
              type="button"
              variant="primary"
              onClick={() => handleSend()}
              loading={sendLoading}
            >
              {t('send')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewApplicatons;
